import { Modal, Button, Alert } from "flowbite-react";
import { HiExclamation, HiCheckCircle, HiXCircle } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { BulkApprovePreCheckResult } from "@/api/kbApi/kbIssue";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  preCheckResult: BulkApprovePreCheckResult | null;
  isLoading: boolean;
  selectedCount: number;
}

function truncate(text: string, max = 60) {
  return text.length > max ? text.substring(0, max) + "..." : text;
}

export default function BulkApproveDialog({
  open,
  onClose,
  onConfirm,
  preCheckResult,
  isLoading,
  selectedCount,
}: Props) {
  const { t } = useTranslation(["knowledgeBase", "common"]);

  if (!preCheckResult) return null;

  const {
    issuesWithoutSolutions,
    issuesWithUnapprovedSolutions,
    unapprovedSolutions,
    approvableIssues,
  } = preCheckResult;

  const hasIssuesWithoutSolutions = issuesWithoutSolutions.length > 0;
  const hasUnapprovedSolutions = unapprovedSolutions.length > 0;
  const approvableCount = approvableIssues.length;

  return (
    <Modal show={open} onClose={onClose} size="xl">
      <Modal.Header>{t("knowledgeBase:bulkApprove")}</Modal.Header>
      <Modal.Body>
        <div className="space-y-5">
          <p className="text-gray-700 dark:text-gray-300">
            {t("knowledgeBase:bulkApproveSelected", { count: selectedCount })}
          </p>

          {/* Issues without solutions */}
          {hasIssuesWithoutSolutions && (
            <Alert color="failure" icon={HiXCircle}>
              <span className="font-medium">{t("knowledgeBase:noSolutionWarning")}</span>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                {t("knowledgeBase:noSolutionWarningDetail", {
                  count: issuesWithoutSolutions.length,
                })}
              </p>
              <ul className="mt-2 space-y-1">
                {issuesWithoutSolutions.map((issue) => (
                  <li
                    key={issue.id}
                    className="flex items-center gap-2 rounded bg-red-50 dark:bg-red-900/20 px-3 py-1.5 text-sm"
                  >
                    <HiXCircle className="h-4 w-4 shrink-0 text-red-500" />
                    <span className="text-gray-800 dark:text-gray-200 truncate">
                      {truncate(issue.text)}
                    </span>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Approvable issues */}
          {approvableCount > 0 && (
            <Alert color="success" icon={HiCheckCircle}>
              <span className="font-medium">
                {t("knowledgeBase:approvableIssues", { count: approvableCount })}
              </span>
              <ul className="mt-2 space-y-1">
                {approvableIssues.map((issue) => (
                  <li
                    key={issue.id}
                    className="flex items-center gap-2 rounded bg-green-50 dark:bg-green-900/20 px-3 py-1.5 text-sm"
                  >
                    <HiCheckCircle className="h-4 w-4 shrink-0 text-green-500" />
                    <span className="text-gray-800 dark:text-gray-200 truncate">
                      {truncate(issue.text)}
                    </span>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Unapproved solutions info */}
          {hasUnapprovedSolutions && approvableCount > 0 && (
            <Alert color="warning" icon={HiExclamation}>
              <span className="font-medium">
                {t("knowledgeBase:unapprovedSolutionsWarning")}
              </span>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                {t("knowledgeBase:unapprovedSolutionsAutoApprove", {
                  count: unapprovedSolutions.length,
                })}
              </p>
              <ul className="mt-2 space-y-1">
                {unapprovedSolutions.map((solution) => (
                  <li
                    key={solution.id}
                    className="flex items-center gap-2 rounded bg-yellow-50 dark:bg-yellow-900/20 px-3 py-1.5 text-sm"
                  >
                    <HiExclamation className="h-4 w-4 shrink-0 text-yellow-500" />
                    <span className="text-gray-800 dark:text-gray-200 truncate">
                      {truncate(solution.text)}
                    </span>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {/* No approvable issues */}
          {approvableCount === 0 && (
            <Alert color="failure" icon={HiXCircle}>
              <span>{t("knowledgeBase:noApprovableIssues")}</span>
            </Alert>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {approvableCount > 0 && (
          <Button
            color="success"
            onClick={onConfirm}
            isProcessing={isLoading}
          >
            <HiCheckCircle className="mr-2 h-5 w-5" />
            {t("knowledgeBase:confirmApprove", { count: approvableCount })}
          </Button>
        )}
        <Button color="gray" onClick={onClose}>
          {t("common:cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
